import { DATE_FORMAT_API } from '@constants/date';
import { Box, Stack, TextField, Typography } from '@mui/material';
import DatePicker from 'components/DatePicker/DatePicker';
import { format } from 'date-fns';
import { FC, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FileExcelIcon } from '../../../../../../../../components/SvgIcons/FileExcelIcon';
import { FilePdfIcon } from '../../../../../../../../components/SvgIcons/FilePdfIcon';
import { FooterExtraButtons } from '../../../../../../components/FooterButtons';
import { DailyReportFormModel } from '../../models';

const commentBoxStyles = {
  '& ul, & ol': {
    margin: '0.5em 0',
    paddingLeft: '2em',
  },
  '& p': {
    margin: '0.5em 0',
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    margin: '1em 0',
  },
  '& td, & th': {
    border: '1px solid #ddd',
    padding: '8px',
  },
  '& blockquote': {
    borderLeft: '3px solid #ddd',
    margin: '1em 0',
    padding: '0.5em 1em',
    backgroundColor: 'rgba(0,0,0,0.03)',
  },
};

interface FilterFormProps {
  onSubmit: (data: DailyReportFormModel) => void;
  onSave: (data: DailyReportFormModel) => void;
  onSaveAndExport: (data: DailyReportFormModel) => void;
  isSaving?: boolean;
  isExporting?: boolean;
  reset: () => void;
}

export const FilterForm: FC<FilterFormProps> = ({
  onSubmit,
  onSave,
  onSaveAndExport,
  isSaving = false,
  isExporting = false,
  reset,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<DailyReportFormModel>();

  const [isEditing, setIsEditing] = useState(false);
  const formValues = watch();

  return (
    <Box
      sx={{
        p: 0,
        height: '100%',
      }}
    >
      <Stack direction={'column'} spacing={0} sx={{ width: '100%', height: '100%' }}>
        <Box sx={{ backgroundColor: 'colors.gray.o10', px: 2 }}>
          <Stack direction={'row'} alignItems={'center'} sx={{ height: '100%' }}>
            <Typography
              align="center"
              color="secondary"
              variant="inter18"
              fontWeight={'fontWeightBold'}
              ml={1.2}
            >
              Frontdesk . Daily Report
            </Typography>
          </Stack>
        </Box>
        <Box
          component="form"
          sx={{
            flexGrow: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between',
          }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Stack spacing={2} sx={{ width: '100%' }}>
            <DatePicker
              name="day"
              label="Day"
              value={formValues?.day}
              onChange={(value: string) =>
                setValue('day', format(new Date(value), DATE_FORMAT_API))
              }
              error={Boolean(errors?.day)}
              helperText={errors?.day?.message}
              renderInput={(params: any) => <TextField variant="standard" {...params} />}
            />

            <Controller
              control={control}
              name="comment"
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value ?? ''}
                  multiline
                  minRows={3}
                  onChange={e => onChange(e.target.value)}
                  variant="standard"
                  fullWidth
                  label={'Comment'}
                />
              )}
            />

            {/* <Controller
              control={control}
              name="comment"
              render={({ field: { value, onChange } }) => (
                <>
                  <Paper
                    elevation={1}
                    sx={{
                      // p: 2,
                      position: 'relative',
                      minHeight: '100px',
                      backgroundColor: 'colors.gray.o10',
                      '& .content': {
                        pt: 2,
                        ...commentBoxStyles,
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle1" color="text.secondary">
                        Comment
                      </Typography>
                    </Stack>
                    <Box
                      className="content"
                      sx={{
                        backgroundColor: 'common.white',
                        position: 'relative',
                        p: 1,
                        px: 2,
                        ':hover': {
                          borderBottom: '2px solid',
                          borderColor: 'colors.purple.o70',
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => setIsEditing(true)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'primary.main',
                          },
                          position: 'absolute',
                          top: 10,
                          right: 10,
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      {value ? (
                        <Box sx={{ pt: 2 }} dangerouslySetInnerHTML={{ __html: value }} />
                      ) : (
                        <Typography color="text.secondary" sx={{ fontStyle: 'italic', pt: 2 }}>
                          No comment yet. Click the edit button to add one.
                        </Typography>
                      )}
                    </Box>
                  </Paper>

                  <RootDialog
                    maxWidth="md"
                    open={isEditing}
                    onClose={() => setIsEditing(false)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <RootDialogTitle
                      onClose={() => setIsEditing(false)}
                      titles={{
                        title: 'Edit Comment',
                        subTitle: 'Reports . Reservation . Daily Report',
                      }}
                    />
                    <Box
                      sx={{
                        // width: '80%',
                        // maxWidth: 800,
                        width: '100%',
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        outline: 'none',
                        maxHeight: '90vh',
                        overflow: 'auto',
                      }}
                    >
                      <FormLabel htmlFor="comment">Comment</FormLabel>
                      <Editor
                        apiKey="n6bpre97291vxsnjkdiruumb9kpzlxy40sszkvftvp74xi2v"
                        value={value}
                        onEditorChange={onChange}
                        id="comment"
                        init={{
                          height: 400,
                          menubar: false,
                          width: '100%',
                          plugins: [
                            'advlist',
                            'autolink',
                            'lists',
                            'link',
                            'charmap',
                            'preview',
                            'searchreplace',
                            'visualblocks',
                            'code',
                            'fullscreen',
                            'insertdatetime',
                            'table',
                            'code',
                            'help',
                            'wordcount',
                          ],
                          toolbar:
                            'undo redo | blocks | ' +
                            'bold italic | alignleft aligncenter ' +
                            'alignright alignjustify | bullist numlist | ' +
                            'removeformat | help',
                          content_style: `
                                  body {
                                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
                                    font-size: 14px;
                                    line-height: 1.5;
                                  }
                                  p { margin: 0.5em 0; }
                                  table { border-collapse: collapse; }
                                  table td, table th { border: 1px solid #ddd; padding: 8px; }
                                `,
                        }}
                      />
                    </Box>
                    <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
                      <Button variant="text" onClick={() => setIsEditing(false)}>
                        Cancel
                      </Button>
                      <Button variant="contained" onClick={() => setIsEditing(false)}>
                        Done
                      </Button>
                    </Stack>
                  </RootDialog>
                </>
              )}
            /> */}
          </Stack>
        </Box>
        <FooterExtraButtons
          onSubmit={file_type => {
            setValue('file_type', file_type);
            handleSubmit(onSubmit)();
          }}
          onReset={reset}
          exportFileTypes={[
            {
              label: 'PDF',
              value: 'pdf',
              icon: <FilePdfIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
            {
              label: 'EXCEL',
              value: 'excel',
              icon: <FileExcelIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
          ]}
        />
      </Stack>
    </Box>
  );
};
